/**
 * Northern Shopper — saved carts (cloud sync + named snapshots)
 */
(function () {
	"use strict";

	var NS = window.NorthernShopper;
	if (!NS) return;

	var PROXY_PREFIX = "/apps/northern-shopper-proxy";
	var DEBOUNCE_MS = 1000;
	var RESOLVED_KEY = "nsSavedCartResolved";
	var SKIPPED_KEY = "nsSavedCartsSkipped";
	var LOGGED_IN_KEY = "nsLoggedInCustomer";
	var DEVICE_ID_KEY = "nsDeviceId";
	var LOAD_PARAM = "ns_load_cart";

	var savedState = { cloud: null, named: [] };
	var lastSavedFingerprint = "";
	var latestLines = null;
	var hydrating = false;
	var prompting = false;
	var pendingTarget = null;
	var saveTimer = null;
	var initialized = false;

	function strings() {
		return (window.NorthernShopperStrings && window.NorthernShopperStrings.savedCarts) || {};
	}

	function t(key, fallback) {
		var value = strings()[key];
		return value == null || value === "" ? fallback : value;
	}

	function getRootUrl() {
		var root = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.root_url) || (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
		return root.endsWith("/") ? root : root + "/";
	}

	function cartUrl(path) {
		return getRootUrl() + path;
	}

	function loggedInCustomerId() {
		var raw = NS.customerId;
		if (raw == null || raw === false) return "";
		var text = String(raw).trim();
		if (!/^[1-9]\d*$/.test(text)) return "";
		return text;
	}

	// The layout's customer id can belong to a cached render. Ask Shopify to
	// render this visitor's session before calling the saved-cart proxy.
	function confirmSessionCustomer() {
		if (!loggedInCustomerId()) return Promise.resolve("");
		return fetch(getRootUrl() + "?section_id=ns-custom-session", {
			credentials: "same-origin",
			cache: "no-store",
			headers: { Accept: "text/html" },
		})
			.then(function (response) {
				if (!response.ok) throw new Error("Session check failed");
				return response.text();
			})
			.then(function (html) {
				var match = String(html || "").match(/data-ns-customer-id="([0-9]*)"/);
				var sessionId = match && match[1] ? match[1] : "";
				if (!/^[1-9]\d*$/.test(sessionId)) return "";
				NS.customerId = sessionId;
				return sessionId;
			})
			.catch(function (error) {
				console.error("[NorthernShopper] Saved carts session check failed", error);
				return "";
			});
	}

	function proxyUrl(path) {
		var root = getRootUrl().replace(/\/$/, "");
		// Do not add a customer id. Shopify sets logged_in_customer_id from the
		// signed app-proxy session. A theme-supplied id can be swapped for anyone's.
		return root + PROXY_PREFIX + path;
	}

	function toast(type, title, message) {
		if (NS.Toast) NS.Toast.show({ type: type, title: title, message: message || "", broadcast: false });
	}

	function lineTitle(item) {
		var title = NS.displayProductTitle((item && (item.product_title || item.title)) || "");
		var variant = item && item.variant_title;
		if (variant && variant !== "Default Title") {
			title = title ? title + " — " + variant : variant;
		}
		return title;
	}

	function cartLines(cart) {
		return ((cart && cart.items) || [])
			.map(function (item) {
				return {
					v: Number(item.variant_id),
					q: Number(item.quantity) || 0,
					t: lineTitle(item),
				};
			})
			.filter(function (line) {
				return line.v && line.q > 0;
			});
	}

	function fingerprint(lines) {
		return (lines || [])
			.slice()
			.sort(function (a, b) {
				return a.v - b.v;
			})
			.map(function (line) {
				return line.v + ":" + line.q;
			})
			.join("|");
	}

	function isEmpty(lines) {
		return !lines || !lines.length;
	}

	function createDeviceId() {
		if (window.crypto && typeof window.crypto.randomUUID === "function") {
			return window.crypto.randomUUID();
		}
		return "ns-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
	}

	function getDeviceId() {
		try {
			var existing = localStorage.getItem(DEVICE_ID_KEY);
			if (existing) return existing;
			var id = createDeviceId();
			localStorage.setItem(DEVICE_ID_KEY, id);
			return id;
		} catch (error) {
			return null;
		}
	}

	function cloudDeviceId() {
		return (savedState.cloud && savedState.cloud.deviceId) || null;
	}

	function isSameDevice() {
		var localId = getDeviceId();
		var remoteId = cloudDeviceId();
		return !!(localId && remoteId && localId === remoteId);
	}

	function rememberLines(lines) {
		latestLines = lines || [];
	}

	function fetchJson(url, options) {
		return fetch(url, Object.assign({ credentials: "same-origin" }, options || {})).then(function (response) {
			return response.json().then(function (body) {
				if (!response.ok) {
					var error = new Error((body && body.message) || "Request failed");
					error.status = response.status;
					error.body = body;
					throw error;
				}
				return body;
			}).catch(function (error) {
				if (error && error.status) throw error;
				if (!response.ok) throw new Error("Request failed");
				return {};
			});
		});
	}

	function fetchCart() {
		if (NS.CardQty && typeof NS.CardQty.fetchCart === "function") {
			return NS.CardQty.fetchCart(true);
		}
		return fetch(cartUrl("cart.js"), { credentials: "same-origin", headers: { Accept: "application/json" } }).then(function (response) {
			if (!response.ok) throw new Error("Cart fetch failed");
			return response.json();
		});
	}

	function publishCart(cart) {
		if (window.FoxTheme && FoxTheme.pubsub && cart) {
			FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
		}
		document.dispatchEvent(new CustomEvent("cart:refresh"));
		if (NS.Pricing && typeof NS.Pricing.recalculateAll === "function") {
			NS.Pricing.recalculateAll();
		}
	}

	function loadSavedCarts() {
		if (!loggedInCustomerId()) return Promise.resolve(savedState);
		return fetchJson(proxyUrl("/customer/saved-carts")).then(function (payload) {
			savedState = {
				cloud: payload.cloud || null,
				named: payload.named || [],
			};
			lastSavedFingerprint = fingerprint((savedState.cloud && savedState.cloud.lines) || []);
			return savedState;
		});
	}

	function cloudBody(lines) {
		var body = { lines: lines || [] };
		var deviceId = getDeviceId();
		if (deviceId) body.deviceId = deviceId;
		return body;
	}

	function putCloud(lines, options) {
		var opts = options || {};
		var payloadLines = lines || [];
		rememberLines(payloadLines);
		var request = {
			method: "POST",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify(cloudBody(payloadLines)),
		};
		if (opts.keepalive) request.keepalive = true;
		return fetchJson(proxyUrl("/customer/saved-carts/cloud"), request).then(function (payload) {
			savedState.cloud = payload.cloud || null;
			savedState.named = payload.named || savedState.named;
			lastSavedFingerprint = fingerprint((savedState.cloud && savedState.cloud.lines) || []);
			return payload;
		});
	}

	function createNamed(name, lines) {
		return fetchJson(proxyUrl("/customer/saved-carts/named"), {
			method: "POST",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify({ name: name || "", lines: lines || [] }),
		}).then(function (payload) {
			savedState.cloud = payload.cloud || savedState.cloud;
			savedState.named = payload.named || [];
			return payload;
		});
	}

	function addItems(items) {
		if (!items.length) {
			return Promise.resolve({ skipped: [] });
		}
		return fetch(cartUrl("cart/add.js"), {
			method: "POST",
			credentials: "same-origin",
			headers: { "Content-Type": "application/json", Accept: "application/json" },
			body: JSON.stringify({ items: items }),
		}).then(function (response) {
			return response.json().then(function (body) {
				if (response.ok && !(body && (body.status === 422 || body.errors))) {
					return { skipped: [] };
				}
				return addItemsOneByOne(items);
			});
		}).catch(function () {
			return addItemsOneByOne(items);
		});
	}

	function addItemsOneByOne(items) {
		var skipped = [];
		var index = 0;

		function next() {
			if (index >= items.length) {
				return { skipped: skipped };
			}
			var item = items[index];
			index += 1;
			return fetch(cartUrl("cart/add.js"), {
				method: "POST",
				credentials: "same-origin",
				headers: { "Content-Type": "application/json", Accept: "application/json" },
				body: JSON.stringify({ id: item.id, quantity: item.quantity }),
			}).then(function (response) {
				if (!response.ok) skipped.push(item);
				return next();
			}).catch(function () {
				skipped.push(item);
				return next();
			});
		}

		return next();
	}

	function clearCart() {
		return fetch(cartUrl("cart/clear.js"), {
			method: "POST",
			credentials: "same-origin",
			headers: { Accept: "application/json" },
		}).then(function (response) {
			if (!response.ok) throw new Error("Unable to clear cart");
			return response.json();
		});
	}

	function toAddItems(lines) {
		return (lines || []).map(function (line) {
			return { id: line.v, quantity: line.q, title: line.t || "" };
		});
	}

	function rememberSkipped(skipped) {
		var entries = (skipped || []).map(function (item) {
			return { v: item.id || item.v, t: item.title || item.t || ("#" + (item.id || item.v)) };
		});
		try {
			if (entries.length) sessionStorage.setItem(SKIPPED_KEY, JSON.stringify(entries));
			else sessionStorage.removeItem(SKIPPED_KEY);
		} catch (error) {
			/* ignore */
		}
		renderSkipped(entries);
		if (entries.length) {
			toast("warning", t("skippedTitle", "Some items couldn’t be added"), entries.map(function (entry) { return entry.t; }).join(", "));
		}
	}

	function renderSkipped(entries) {
		var list = entries;
		if (!list) {
			try {
				list = JSON.parse(sessionStorage.getItem(SKIPPED_KEY) || "[]");
			} catch (error) {
				list = [];
			}
		}
		document.querySelectorAll("[data-ns-skipped-banner]").forEach(function (banner) {
			var titleEl = banner.querySelector("[data-ns-skipped-title]");
			var messageEl = banner.querySelector("[data-ns-skipped-message]");
			var listEl = banner.querySelector("[data-ns-skipped-list]");
			if (titleEl) titleEl.textContent = t("skippedTitle", "Some items couldn’t be added");
			if (messageEl) messageEl.textContent = t("skippedMessage", "These items are no longer available:");
			if (listEl) {
				listEl.innerHTML = "";
				(list || []).forEach(function (entry) {
					var li = document.createElement("li");
					li.textContent = entry.t || ("#" + entry.v);
					listEl.appendChild(li);
				});
			}
			banner.hidden = !(list && list.length);
		});
	}

	function hydrate(lines, mode) {
		var items = toAddItems(lines);
		hydrating = true;
		var start = mode === "replace" ? clearCart() : Promise.resolve();
		return start
			.then(function () {
				return addItems(items);
			})
			.then(function (result) {
				rememberSkipped(result.skipped || []);
				return fetchCart();
			})
			.then(function (cart) {
				publishCart(cart);
				return cart;
			})
			.finally(function () {
				hydrating = false;
			});
	}

	function updateSaveButtons(cart) {
		if (!cart) return;
		var count = typeof cart.item_count === "number" ? cart.item_count : cart.items ? cartLines(cart).length : null;
		if (count == null) return;
		document.querySelectorAll("[data-ns-save-cart], [data-ns-save-cart-open]").forEach(function (button) {
			button.disabled = !count;
		});
	}

	function fillNameInputs() {
		document.querySelectorAll("[data-ns-save-cart-name]").forEach(function (input) {
			if (!input.value) input.value = defaultName();
		});
	}

	function openModal(id) {
		var modal = document.getElementById(id);
		if (!modal) return;
		modal.hidden = false;
		modal.setAttribute("aria-hidden", "false");
		document.body.classList.add("ns-custom-modal-open");
	}

	function closeModal(id) {
		var modal = document.getElementById(id);
		if (!modal) return;
		modal.hidden = true;
		modal.setAttribute("aria-hidden", "true");
		if (!document.querySelector(".ns-custom-modal:not([hidden])")) {
			document.body.classList.remove("ns-custom-modal-open");
		}
	}

	function resolvedKey(deviceFp, savedFp) {
		return deviceFp + ">" + savedFp;
	}

	function alreadyResolved(deviceFp, savedFp) {
		try {
			return sessionStorage.getItem(RESOLVED_KEY) === resolvedKey(deviceFp, savedFp);
		} catch (error) {
			return false;
		}
	}

	function markResolved(deviceFp, savedFp) {
		try {
			sessionStorage.setItem(RESOLVED_KEY, resolvedKey(deviceFp, savedFp));
		} catch (error) {
			/* ignore */
		}
	}

	function lineCount(lines) {
		return (lines || []).reduce(function (sum, line) {
			return sum + (Number(line.q) || 0);
		}, 0);
	}

	function restoreLead(count) {
		if (count === 1) {
			return t("restoreLeadOne", "You have a previous cart with 1 item. Do you want to continue with the last session?");
		}
		return t("restoreLeadOther", "You have a previous cart with {{ count }} items. Do you want to continue with the last session?").replace("{{ count }}", String(count));
	}

	function showConflict(lead) {
		var modal = document.getElementById("ns-saved-carts-conflict");
		if (!modal) return Promise.resolve("no");
		var leadEl = modal.querySelector(".ns-saved-carts-modal__lead");
		if (leadEl) {
			if (!modal.dataset.nsDefaultLead) modal.dataset.nsDefaultLead = leadEl.textContent;
			leadEl.textContent = lead || modal.dataset.nsDefaultLead;
		}
		prompting = true;
		openModal("ns-saved-carts-conflict");
		return new Promise(function (resolve) {
			pendingTarget = { resolve: resolve };
		});
	}

	function finishPrompt(choice) {
		var pending = pendingTarget;
		pendingTarget = null;
		prompting = false;
		closeModal("ns-saved-carts-conflict");
		closeModal("ns-saved-carts-load");
		if (pending && pending.resolve) pending.resolve(choice);
	}

	function showLoadConflict() {
		var modal = document.getElementById("ns-saved-carts-load");
		if (!modal) return Promise.resolve("cancel");
		prompting = true;
		openModal("ns-saved-carts-load");
		return new Promise(function (resolve) {
			pendingTarget = { resolve: resolve };
		});
	}

	function defaultName() {
		var now = new Date();
		var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
		return t("defaultName", "Saved cart") + " · " + months[now.getMonth()] + " " + now.getDate() + ", " + now.getFullYear();
	}

	function scheduleCloudSave(lines) {
		if (!loggedInCustomerId() || hydrating || prompting) return;
		if (lines) rememberLines(lines);
		var fp = fingerprint(lines);
		if (fp === lastSavedFingerprint) return;
		clearTimeout(saveTimer);
		saveTimer = setTimeout(function () {
			saveTimer = null;
			flushCloudSave(lines);
		}, DEBOUNCE_MS);
	}

	function flushCloudSave(lines, options) {
		if (!loggedInCustomerId() || hydrating || prompting) return Promise.resolve();
		clearTimeout(saveTimer);
		saveTimer = null;
		var payload = lines != null ? lines : latestLines;
		if (!payload) return Promise.resolve();
		if (fingerprint(payload) === lastSavedFingerprint) return Promise.resolve();
		return putCloud(payload, options).catch(function (error) {
			console.error("[NorthernShopper] Cloud cart save failed", error);
		});
	}

	function applyChoice(choice, deviceLines, savedLines) {
		var deviceFp = fingerprint(deviceLines);
		var savedFp = fingerprint(savedLines);
		if (choice === "yes") {
			return hydrate(savedLines, "replace").then(function (cart) {
				markResolved(fingerprint(cartLines(cart)), savedFp);
				toast("success", t("loaded", "Saved cart loaded"));
				return putCloud(cartLines(cart));
			});
		}
		return createNamed(defaultName(), savedLines)
			.then(function () {
				return putCloud(deviceLines);
			})
			.then(function () {
				markResolved(deviceFp, savedFp);
				toast("success", t("namedSaved", "Cart saved"));
			})
			.catch(function (error) {
				console.error("[NorthernShopper] Could not keep the other session", error);
				toast("error", t("saveError", "We couldn’t save your cart. Please try again."));
			});
	}

	function handleConflict(deviceLines, savedLines, lead) {
		if (alreadyResolved(fingerprint(deviceLines), fingerprint(savedLines))) {
			return Promise.resolve();
		}
		return showConflict(lead).then(function (choice) {
			return applyChoice(choice, deviceLines, savedLines);
		});
	}

	function applyLoadChoice(choice, savedLines) {
		if (choice !== "merge" && choice !== "replace") {
			return Promise.resolve();
		}
		return hydrate(savedLines, choice === "replace" ? "replace" : "merge").then(function (cart) {
			toast("success", choice === "merge" ? t("merged", "Carts merged") : t("loaded", "Saved cart loaded"));
			return putCloud(cartLines(cart));
		});
	}

	function findNamed(id) {
		var named = savedState.named || [];
		var target = String(id || "").replace(/-/g, "").toLowerCase();
		for (var i = 0; i < named.length; i++) {
			var cartId = String(named[i].id || named[i].cartId || "").replace(/-/g, "").toLowerCase();
			if (named[i].id === id || named[i].cartId === id || (target && cartId === target)) return named[i];
		}
		return null;
	}

	function linesForLoadId(id) {
		if (!id || id === "cloud") {
			return (savedState.cloud && savedState.cloud.lines) || [];
		}
		var named = findNamed(id);
		return named ? named.lines : null;
	}

	function stripLoadParam() {
		if (!window.history || !window.history.replaceState) return;
		var url = new URL(window.location.href);
		if (!url.searchParams.has(LOAD_PARAM)) return;
		url.searchParams.delete(LOAD_PARAM);
		window.history.replaceState({}, "", url.pathname + url.search + url.hash);
	}

	function handleLoadIntent(id, deviceLines) {
		var savedLines = linesForLoadId(id);
		stripLoadParam();
		if (!savedLines) {
			toast("error", t("notFound", "That saved cart is no longer available."));
			return Promise.resolve();
		}
		if (isEmpty(deviceLines)) {
			return hydrate(savedLines, "replace").then(function (cart) {
				toast("success", t("loaded", "Saved cart loaded"));
				return putCloud(cartLines(cart));
			});
		}
		if (fingerprint(deviceLines) === fingerprint(savedLines)) {
			return Promise.resolve();
		}
		return showLoadConflict().then(function (choice) {
			return applyLoadChoice(choice, savedLines);
		});
	}

	function handleLoginSync(deviceLines) {
		var cloudLines = (savedState.cloud && savedState.cloud.lines) || [];
		rememberLines(deviceLines);
		if (isEmpty(deviceLines) && isEmpty(cloudLines)) return Promise.resolve();
		if (isEmpty(cloudLines) && !isEmpty(deviceLines)) {
			return putCloud(deviceLines);
		}
		if (isEmpty(deviceLines) && !isEmpty(cloudLines)) {
			return handleConflict(deviceLines, cloudLines, restoreLead(lineCount(cloudLines)));
		}
		if (fingerprint(deviceLines) === fingerprint(cloudLines)) {
			lastSavedFingerprint = fingerprint(deviceLines);
			if (isSameDevice()) return Promise.resolve();
			// Same lines from another/missing writer — stamp this browser's id.
			return putCloud(deviceLines);
		}
		if (isSameDevice()) {
			return putCloud(deviceLines);
		}
		return handleConflict(deviceLines, cloudLines);
	}

	function saveCurrentAsNamed(name) {
		return fetch(cartUrl("cart.js"), { credentials: "same-origin", headers: { Accept: "application/json" } })
			.then(function (response) {
				return response.json();
			})
			.then(function (cart) {
				var lines = cartLines(cart);
				if (!lines.length) {
					toast("warning", t("emptySave", "Add items before saving a cart."));
					return false;
				}
				return createNamed(name || defaultName(), lines).then(function () {
					toast("success", t("namedSaved", "Cart saved"));
					return true;
				});
			});
	}

	function closeSaveUi(button) {
		var details = button && button.closest('[is="accordion-details"]');
		if (details && typeof details.close === "function") details.close();
	}

	var naming = false;

	function onSaveClick(event) {
		var button = event.target && event.target.closest && event.target.closest("[data-ns-save-cart]");
		if (!button || button.disabled || naming || !loggedInCustomerId()) return;
		event.preventDefault();
		var wrap = button.closest(".ns-custom-save-cart");
		var input = wrap && wrap.querySelector("[data-ns-save-cart-name]");
		var name = input ? input.value : "";
		naming = true;
		button.disabled = true;
		saveCurrentAsNamed(name)
			.then(function (saved) {
				if (!saved || !button.isConnected) return;
				if (input && input.isConnected) input.value = defaultName();
				closeSaveUi(button);
			})
			.catch(function (error) {
				console.error("[NorthernShopper] Named cart save failed", error);
				toast("error", t("saveError", "We couldn’t save your cart. Please try again."));
			})
			.finally(function () {
				naming = false;
				fetchCart()
					.then(updateSaveButtons)
					.catch(function () {
						if (button.isConnected) button.disabled = false;
					});
			});
	}

	function bindUi() {
		document.querySelectorAll("[data-ns-saved-carts-yes]").forEach(function (button) {
			if (button.dataset.nsBound) return;
			button.dataset.nsBound = "1";
			button.addEventListener("click", function () {
				finishPrompt("yes");
			});
		});
		document.querySelectorAll("[data-ns-saved-carts-no]").forEach(function (button) {
			if (button.dataset.nsBound) return;
			button.dataset.nsBound = "1";
			button.addEventListener("click", function () {
				finishPrompt("no");
			});
		});
		document.querySelectorAll("[data-ns-saved-carts-load-merge]").forEach(function (button) {
			if (button.dataset.nsBound) return;
			button.dataset.nsBound = "1";
			button.addEventListener("click", function () {
				finishPrompt("merge");
			});
		});
		document.querySelectorAll("[data-ns-saved-carts-load-replace]").forEach(function (button) {
			if (button.dataset.nsBound) return;
			button.dataset.nsBound = "1";
			button.addEventListener("click", function () {
				finishPrompt("replace");
			});
		});
		document.querySelectorAll("[data-ns-saved-carts-load-cancel]").forEach(function (button) {
			if (button.dataset.nsBound) return;
			button.dataset.nsBound = "1";
			button.addEventListener("click", function () {
				finishPrompt("cancel");
			});
		});
		fillNameInputs();
		document.querySelectorAll("[data-ns-skipped-dismiss]").forEach(function (button) {
			if (button.dataset.nsBound) return;
			button.dataset.nsBound = "1";
			button.addEventListener("click", function () {
				try {
					sessionStorage.removeItem(SKIPPED_KEY);
				} catch (error) {
					/* ignore */
				}
				renderSkipped([]);
			});
		});
	}

	function rememberLogin() {
		try {
			var customerId = loggedInCustomerId();
			if (customerId) sessionStorage.setItem(LOGGED_IN_KEY, customerId);
		} catch (error) {
			/* ignore */
		}
	}

	function wasLoggedIn() {
		try {
			return !!sessionStorage.getItem(LOGGED_IN_KEY);
		} catch (error) {
			return false;
		}
	}

	function forgetLogin() {
		try {
			sessionStorage.removeItem(LOGGED_IN_KEY);
		} catch (error) {
			/* ignore */
		}
	}

	// The cart cookie outlives logout. Remember this tab's customer, and on the
	// first logged-out page load drop only the local cart. The cloud cart stays.
	function clearCartAfterLogout() {
		if (loggedInCustomerId() || !wasLoggedIn()) return;
		clearCart()
			.then(function (cart) {
				forgetLogin();
				publishCart(cart);
			})
			.catch(function (error) {
				console.error("[NorthernShopper] Logout cart clear failed", error);
			});
	}

	function init() {
		if (initialized) {
			bindUi();
			renderSkipped();
			return;
		}
		initialized = true;
		bindUi();
		renderSkipped();
		document.addEventListener("click", onSaveClick, true);
		document.addEventListener("keydown", function (event) {
			if (event.key !== "Enter") return;
			var input = event.target && event.target.closest && event.target.closest("[data-ns-save-cart-name]");
			if (!input) return;
			event.preventDefault();
			var wrap = input.closest(".ns-custom-save-cart");
			var saveBtn = wrap && wrap.querySelector("[data-ns-save-cart]");
			if (saveBtn && !saveBtn.disabled) saveBtn.click();
		});
		document.addEventListener("modal:handleAfterShow", function (event) {
			var modal = event.target;
			if (!modal || !modal.matches || !modal.matches("cart-addon-modal[id^='ns-save-cart-']")) return;
			var input = modal.querySelector("[data-ns-save-cart-name]");
			if (!input) return;
			input.value = defaultName();
			input.focus();
			input.select();
		});
		document.addEventListener("cart:updated", function (event) {
			fillNameInputs();
			updateSaveButtons(event.detail && event.detail.cart);
		});
		clearCartAfterLogout();

		if (!loggedInCustomerId()) return;

		confirmSessionCustomer().then(function (customerId) {
			if (!customerId) {
				NS.customerId = null;
				return;
			}
			rememberLogin();

			var params = new URLSearchParams(window.location.search);
			var loadId = params.get(LOAD_PARAM);

			Promise.all([loadSavedCarts(), fetch(cartUrl("cart.js"), { credentials: "same-origin", headers: { Accept: "application/json" } }).then(function (response) { return response.json(); })])
				.then(function (results) {
					var cart = results[1] || {};
					var deviceLines = cartLines(cart);
					rememberLines(deviceLines);
					updateSaveButtons(cart);
					if (loadId) return handleLoadIntent(loadId, deviceLines);
					return handleLoginSync(deviceLines);
				})
				.catch(function (error) {
					console.error("[NorthernShopper] Saved carts init failed", error);
					if (loadId) toast("error", t("loadError", "We couldn’t load that cart. Please try again."));
				});

			if (window.FoxTheme && FoxTheme.pubsub) {
				FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
					// Cart section HTML is swapped by a later subscriber. Sync after that swap.
					Promise.resolve().then(function () {
						bindUi();
						fillNameInputs();
						var cart = event && event.cart;
						if (cart) updateSaveButtons(cart);
					});
					var cart = event && event.cart;
					if (!loggedInCustomerId() || hydrating || prompting) return;
					var lines = cart ? cartLines(cart) : null;
					if (lines) rememberLines(lines);
					scheduleCloudSave(lines);
				});
			}

			window.addEventListener("pagehide", function () {
				if (!loggedInCustomerId() || hydrating || prompting) return;
				flushCloudSave(latestLines, { keepalive: true });
			});
			document.addEventListener("visibilitychange", function () {
				if (document.visibilityState === "hidden") flushCloudSave(latestLines, { keepalive: true });
			});
		});
	}

	document.addEventListener("DOMContentLoaded", init);
	if (document.readyState !== "loading") init();

	NS.SavedCarts = {
		refresh: loadSavedCarts,
	};
})();
